import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import api from "../../Utils/Axios";
import "./ChatSection.css";

const REFRESH_INTERVAL_MS = 7000;

const ChatSection = ({ mode }) => {
  const [chats, setChats] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState("");
  const [messageText, setMessageText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const bottomRef = useRef(null);

  const isAdminMode = mode === "admin";

  const selectedChat = useMemo(
    () => chats.find((chat) => chat._id === selectedChatId) || null,
    [chats, selectedChatId]
  );

  const normalizeAdminChats = (chatList) =>
    (chatList || []).map((chat) => ({
      ...chat,
      messages: Array.isArray(chat.messages) ? chat.messages : [],
    }));

  const normalizeHospitalChat = (chat) => [
    {
      ...chat,
      messages: Array.isArray(chat?.messages) ? chat.messages : [],
    },
  ];

  const fetchChats = async (showLoader = false) => {
    if (showLoader) {
      setLoading(true);
    }

    try {
      if (isAdminMode) {
        const res = await api.get("/api/chat/admin/all");
        const list = normalizeAdminChats(res.data?.chats);
        setChats(list);

        if (!selectedChatId && list.length > 0) {
          setSelectedChatId(list[0]._id);
        } else if (selectedChatId && !list.find((item) => item._id === selectedChatId)) {
          setSelectedChatId(list[0]?._id || "");
        }
      } else {
        const res = await api.get("/api/chat/hospital/me");
        const list = normalizeHospitalChat(res.data?.chat);
        setChats(list);
        setSelectedChatId(list[0]?._id || "");
      }
      setError("");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load chats");
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  };

  const upsertChat = (nextChat) => {
    setChats((prev) => {
      const existingIndex = prev.findIndex((chat) => chat._id === nextChat._id);
      if (existingIndex === -1) {
        return [nextChat, ...prev];
      }

      const updated = [...prev];
      updated[existingIndex] = nextChat;
      updated.sort((a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt));
      return updated;
    });
  };

  const handleSend = async (event) => {
    event.preventDefault();
    if (!selectedChat) return;

    const text = messageText.trim();
    if (!text) return;

    setSending(true);
    try {
      const endpoint = isAdminMode
        ? `/api/chat/admin/${selectedChat._id}/message`
        : "/api/chat/hospital/me/message";
      const res = await api.post(endpoint, { text });
      const updatedChat = res.data?.chat;
      if (updatedChat?._id) {
        upsertChat(updatedChat);
        setSelectedChatId(updatedChat._id);
      }
      setMessageText("");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const getChatTitle = (chat) => {
    if (!isAdminMode) return "Admin Support";
    if (chat?.hospitalId?.name) return chat.hospitalId.name;
    if (typeof chat?.hospitalId === "string") return `Hospital ${chat.hospitalId.slice(-6)}`;
    return "Hospital";
  };

  const getChatSubtitle = (chat) => {
    if (!isAdminMode) return "Chat with platform admin";
    return chat?.hospitalId?.email || "No email";
  };

  const getLastMessage = (chat) => {
    const last = chat?.messages?.[chat.messages.length - 1];
    return last?.text || "No messages yet";
  };

  const getUnreadForAdmin = (chat) =>
    (chat?.messages || []).filter((msg) => msg.senderRole === "hospital" && !msg.isRead).length;

  useEffect(() => {
    fetchChats(true);
  }, [isAdminMode]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchChats(false);
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isAdminMode, selectedChatId]);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [selectedChat]);

  return (
    <div className="col-lg-12">
      <div className="dashboard-inner">
        <div className="chat-section-header">
          <h4>{isAdminMode ? "Hospital Chat Inbox" : "Support Chat"}</h4>
          <p>{isAdminMode ? "Reply to hospitals from one place." : "Message admin for help."}</p>
        </div>

        {loading ? (
          <p>Loading chat...</p>
        ) : error ? (
          <p>{error}</p>
        ) : (
          <div className="chat-layout">
            <div className="chat-conversation-list">
              {chats.length === 0 ? (
                <div className="chat-empty-list">No chats available.</div>
              ) : (
                chats.map((chat) => {
                  const isActive = chat._id === selectedChatId;
                  const unread = isAdminMode ? getUnreadForAdmin(chat) : 0;

                  return (
                    <button
                      key={chat._id}
                      className={`chat-thread-item ${isActive ? "active" : ""}`}
                      onClick={() => setSelectedChatId(chat._id)}
                      type="button"
                    >
                      <div className="chat-thread-head">
                        <h6>{getChatTitle(chat)}</h6>
                        {unread > 0 && <span className="chat-unread">{unread}</span>}
                      </div>
                      <p className="chat-thread-subtitle">{getChatSubtitle(chat)}</p>
                      <p className="chat-thread-last">{getLastMessage(chat)}</p>
                    </button>
                  );
                })
              )}
            </div>

            <div className="chat-window">
              {!selectedChat ? (
                <div className="chat-empty-window">Select a conversation to start messaging.</div>
              ) : (
                <>
                  <div className="chat-window-top">
                    <h5>{getChatTitle(selectedChat)}</h5>
                    <span>{getChatSubtitle(selectedChat)}</span>
                  </div>

                  <div className="chat-messages">
                    {(selectedChat.messages || []).length === 0 ? (
                      <div className="chat-empty-window">No messages yet.</div>
                    ) : (
                      (selectedChat.messages || []).map((msg, index) => {
                        const ownMessage = isAdminMode
                          ? msg.senderRole === "admin"
                          : msg.senderRole === "hospital";

                        return (
                          <div
                            className={`chat-bubble-row ${ownMessage ? "mine" : "theirs"}`}
                            key={`${msg.sentAt || "msg"}-${index}`}
                          >
                            <div className="chat-bubble">
                              <p>{msg.text}</p>
                              <span>
                                {msg.sentAt ? new Date(msg.sentAt).toLocaleString() : "Just now"}
                              </span>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={bottomRef} />
                  </div>

                  <form className="chat-input-row" onSubmit={handleSend}>
                    <input
                      type="text"
                      placeholder="Type your message..."
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      maxLength={1000}
                    />
                    <button className="primry-btn-1" type="submit" disabled={sending}>
                      {sending ? "Sending..." : "Send"}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatSection;
